import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Eye, EyeOff, Loader2, UserPlus, Wand2 } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { useQuotaExceededDialog } from '@/components/billing/quota-exceeded-dialog'
import { useInviteMember, useMyPermissions, useRoles } from '@/hooks/use-settings'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { useOrgTeams } from '@/hooks/use-ai'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { useAuthStore } from '@/stores/auth-store'
import {
  buildRoleOptions,
  channelBriefsFromApi,
  defaultRoleValue,
  displayVnPhone,
  emailFormatError,
  generateStrongPassword,
  loginUrlFor,
  memberPhoneError,
  normalizeEmail,
  rolePayload,
  selectedChannelBriefs,
  suggestEmail,
  toChannelOptions,
  type NewMemberInfo,
} from './add-member-helpers'
import { MemberCreatedDialog } from './member-created-dialog'
import { MemberChannelPicker } from './member-channel-picker'
import { AccessLevelLegend, AccessLevelSelect } from '@/components/shared/access-level-select'
import { DEFAULT_ACCESS_LEVEL, type AccessLevel } from '@/lib/channel-access'

const NO_TEAM = '__none__'
const EMPTY = { fullName: '', email: '', phone: '', roleValue: '', password: '', teamId: NO_TEAM, channelIds: [] as string[], channelLevel: DEFAULT_ACCESS_LEVEL as AccessLevel }

const FIELDS = ['email', 'password', 'role', 'phone', 'team', 'channels'] as const
type Field = (typeof FIELDS)[number]
type FieldErrors = Partial<Record<Field, string>>
const isField = (v: unknown): v is Field => typeof v === 'string' && (FIELDS as readonly string[]).includes(v)

/** Thêm nhân viên (người) — API `/settings/team/invite`. Thành công → hộp thoại tóm tắt để chép gửi nhân viên. */
export function AddHumanMemberDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('team')
  const invite = useInviteMember()
  const quotaDialog = useQuotaExceededDialog()
  // Mở từ trang Bắt đầu (bước 5 — Mời đồng nghiệp) → báo xong bước + tự quay lại (sau khi đóng hộp tóm tắt).
  const onboardingReturn = useOnboardingReturn()
  const me = useAuthStore((s) => s.user)
  const perms = useMyPermissions()
  const canViewRoles = !!perms.data?.has('roles.view')
  const roles = useRoles(open && canViewRoles)
  // Kênh phụ trách: cùng quyền với cột "Kênh phụ trách" (POST /zalo-accounts/:id/access). Thiếu quyền → ẩn hẳn.
  const canAssignChannels = !!perms.data?.has('account_access.update')
  const accounts = useZaloAccounts(undefined, true, open && canAssignChannels)
  const channelOptions = useMemo(() => toChannelOptions(accounts.data), [accounts.data])
  const teams = useOrgTeams(open)
  const teamList = teams.data?.teams ?? []

  const [form, setForm] = useState(EMPTY)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [showPw, setShowPw] = useState(false)
  // Email nghi gõ nhầm mà người dùng vẫn chọn giữ — lưu đúng giá trị đã xác nhận.
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null)
  const [emailTouched, setEmailTouched] = useState(false)
  const [created, setCreated] = useState<NewMemberInfo | null>(null)

  const roleOptions = useMemo(
    () => buildRoleOptions(canViewRoles && !roles.isError ? roles.data : null, me?.role),
    [canViewRoles, roles.isError, roles.data, me?.role],
  )
  const roleValue = roleOptions.some((o) => o.value === form.roleValue) ? form.roleValue : defaultRoleValue(roleOptions)
  const selectedRole = roleOptions.find((o) => o.value === roleValue)
  const systemOptions = roleOptions.filter((o) => o.isSystem)
  const customOptions = roleOptions.filter((o) => !o.isSystem)

  const email = normalizeEmail(form.email)
  const formatErr = email ? emailFormatError(email) : null
  const suggestion = !formatErr && email ? suggestEmail(email) : null
  const typoPending = !!suggestion && confirmedEmail !== email
  const emailError = errors.email ?? (emailTouched ? formatErr : null)

  function reset() {
    setForm(EMPTY)
    setErrors({})
    setShowPw(false)
    setConfirmedEmail(null)
    setEmailTouched(false)
  }

  function close() {
    reset()
    onOpenChange(false)
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setEmailTouched(true)
    const fe = emailFormatError(email)
    if (fe) {
      setErrors({ email: fe })
      document.getElementById('hm-email')?.focus()
      return
    }
    if (typoPending) {
      setErrors({ email: t('addHumanMemberDialog.emailTypoError', { suggestion }) })
      return
    }
    const phoneErr = memberPhoneError(form.phone)
    if (phoneErr) {
      setErrors({ phone: phoneErr })
      document.getElementById('hm-phone')?.focus()
      return
    }
    const password = form.password
    if (password && password.length < 8) {
      setErrors({ password: t('addHumanMemberDialog.passwordMinLength') })
      return
    }
    setErrors({})
    // Chỉ gửi kênh còn trong danh sách (phòng kênh bị xoá sau khi chọn) và chỉ khi có quyền.
    const channelIds = canAssignChannels
      ? form.channelIds.filter((id) => channelOptions.some((o) => o.id === id))
      : []
    const teamId = form.teamId !== NO_TEAM && teamList.some((t) => t.id === form.teamId) ? form.teamId : undefined
    try {
      const res = await invite.mutateAsync({
        email,
        fullName: form.fullName.trim() || undefined,
        ...rolePayload(roleValue),
        password: password || undefined,
        phone: form.phone.trim() || undefined,
        teamId,
        ...(channelIds.length ? { channelAccountIds: channelIds, channelAccessLevel: form.channelLevel } : {}),
      })
      setCreated({
        fullName: res.fullName,
        email: res.email,
        roleName: res.roleName || selectedRole?.label || t('common:roles.member'),
        password: res.generatedPassword ?? password,
        loginUrl: loginUrlFor(window.location.origin),
        orgName: me?.org?.name ?? null,
        phone: displayVnPhone(res.phone) || null,
        teamName: res.teamName ?? teamList.find((t) => t.id === teamId)?.name ?? null,
        channels: res.channels ? channelBriefsFromApi(res.channels) : selectedChannelBriefs(channelIds, channelOptions),
      })
      reset()
      onOpenChange(false)
    } catch (err) {
      if (quotaDialog.handle(err)) return
      const data = (err as AxiosError<{ field?: string; error?: string }>)?.response?.data
      const msg = apiError(err)
      if (isField(data?.field)) {
        setErrors({ [data.field]: msg })
      } else {
        toast.error(msg)
      }
    }
  }

  function onSummaryClose() {
    setCreated(null)
    // Tiếp tục luồng trang Bắt đầu SAU khi người dùng đã chép xong thông tin.
    onboardingReturn.complete(5)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('addHumanMemberDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('addHumanMemberDialog.passwordBlankHint')}
              {canAssignChannels ? ` ${t('addHumanMemberDialog.canAssignChannelsHint')}` : ''}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="hm-name">{t('addHumanMemberDialog.fullName')}</Label>
              <Input
                id="hm-name"
                maxLength={120}
                value={form.fullName}
                onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))}
                placeholder={t('addHumanMemberDialog.fullNamePlaceholder')}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="hm-email">Email *</Label>
              <Input
                id="hm-email"
                type="email"
                inputMode="email"
                autoComplete="off"
                maxLength={254}
                aria-invalid={!!emailError}
                aria-describedby="hm-email-hint"
                className={emailError ? 'border-destructive focus:border-destructive focus:ring-destructive/15' : undefined}
                value={form.email}
                onChange={(e) => {
                  setForm((s) => ({ ...s, email: e.target.value }))
                  if (errors.email) setErrors((x) => ({ ...x, email: undefined }))
                }}
                onBlur={() => {
                  setEmailTouched(true)
                  if (form.email && form.email !== email) setForm((s) => ({ ...s, email: normalizeEmail(s.email) }))
                }}
                placeholder="email@company.vn"
              />
              <div id="hm-email-hint" className="space-y-1.5">
                {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                {suggestion && typoPending && (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      <Trans t={t} i18nKey="addHumanMemberDialog.emailTypoAsk" values={{ suggestion }} components={{ b: <b /> }} />
                    </span>
                    <button
                      type="button"
                      className="font-medium text-primary underline-offset-2 hover:underline"
                      onClick={() => {
                        setForm((s) => ({ ...s, email: suggestion }))
                        setErrors((x) => ({ ...x, email: undefined }))
                      }}
                    >
                      {t('addHumanMemberDialog.fixTo', { suggestion })}
                    </button>
                    <span aria-hidden>·</span>
                    <button
                      type="button"
                      className="text-muted-foreground underline-offset-2 hover:underline"
                      onClick={() => {
                        setConfirmedEmail(email)
                        setErrors((x) => ({ ...x, email: undefined }))
                      }}
                    >
                      {t('addHumanMemberDialog.keepThisEmail')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="hm-phone">{t('addHumanMemberDialog.phoneOptional')}</Label>
              <Input
                id="hm-phone"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                maxLength={20}
                aria-invalid={!!errors.phone}
                className={errors.phone ? 'border-destructive focus:border-destructive focus:ring-destructive/15' : undefined}
                value={form.phone}
                onChange={(e) => {
                  setForm((s) => ({ ...s, phone: e.target.value }))
                  if (errors.phone) setErrors((x) => ({ ...x, phone: undefined }))
                }}
                onBlur={() => {
                  const err = memberPhoneError(form.phone)
                  if (err) setErrors((x) => ({ ...x, phone: err }))
                }}
                placeholder="0912345678"
              />
              {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="hm-role">{t('addHumanMemberDialog.role')}</Label>
              <Select
                value={roleValue}
                onValueChange={(v) => {
                  setForm((s) => ({ ...s, roleValue: v }))
                  if (errors.role) setErrors((x) => ({ ...x, role: undefined }))
                }}
              >
                <SelectTrigger id="hm-role">
                  <SelectValue placeholder={roles.isLoading ? t('addHumanMemberDialog.loadingRoles') : t('addHumanMemberDialog.chooseRole')} />
                </SelectTrigger>
                <SelectContent>
                  {customOptions.length > 0 ? (
                    <>
                      <SelectGroup>
                        <SelectLabel>{t('addHumanMemberDialog.systemRoles')}</SelectLabel>
                        {systemOptions.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>{t('addHumanMemberDialog.customRoles')}</SelectLabel>
                        {customOptions.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </>
                  ) : (
                    roleOptions.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.role ? (
                <p className="text-xs text-destructive">{errors.role}</p>
              ) : (
                selectedRole?.description && <p className="text-xs text-muted-foreground">{selectedRole.description}</p>
              )}
            </div>

            {teamList.length > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor="hm-team">{t('addHumanMemberDialog.department')}</Label>
                <Select
                  value={teamList.some((tm) => tm.id === form.teamId) ? form.teamId : NO_TEAM}
                  onValueChange={(v) => {
                    setForm((s) => ({ ...s, teamId: v }))
                    if (errors.team) setErrors((x) => ({ ...x, team: undefined }))
                  }}
                >
                  <SelectTrigger id="hm-team">
                    <SelectValue placeholder={t('addHumanMemberDialog.chooseDepartment')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TEAM}>{t('addHumanMemberDialog.noDepartment')}</SelectItem>
                    {teamList.map((tm) => (
                      <SelectItem key={tm.id} value={tm.id}>
                        {tm.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.team && <p className="text-xs text-destructive">{errors.team}</p>}
              </div>
            )}

            {canAssignChannels && (
              <div className="space-y-1.5">
                <Label>{t('addHumanMemberDialog.channelsOptional')}</Label>
                {(selectedRole?.systemKey === 'admin' || selectedRole?.systemKey === 'manager') && (
                  <p className="text-xs text-muted-foreground">
                    {t('addHumanMemberDialog.roleAlreadyHasAllAccounts', { role: selectedRole.label })}
                  </p>
                )}
                <MemberChannelPicker
                  options={channelOptions}
                  selected={form.channelIds}
                  loading={accounts.isLoading}
                  error={errors.channels}
                  onChange={(ids) => {
                    setForm((s) => ({ ...s, channelIds: ids }))
                    if (errors.channels) setErrors((x) => ({ ...x, channels: undefined }))
                  }}
                />
                {form.channelIds.length > 0 && (
                  <div className="space-y-2 rounded-lg border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium">{t('addHumanMemberDialog.accessLevelOnChannels')}</span>
                      <AccessLevelSelect
                        value={form.channelLevel}
                        onChange={(l) => setForm((s) => ({ ...s, channelLevel: l }))}
                        ariaLabel={t('addHumanMemberDialog.accessLevelOnChannels')}
                      />
                    </div>
                    <AccessLevelLegend />
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="hm-pass">{t('addHumanMemberDialog.passwordOptional')}</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="hm-pass"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    maxLength={72}
                    aria-invalid={!!errors.password}
                    className="pr-9 font-mono"
                    value={form.password}
                    onChange={(e) => {
                      setForm((s) => ({ ...s, password: e.target.value }))
                      if (errors.password) setErrors((x) => ({ ...x, password: undefined }))
                    }}
                    placeholder={t('addHumanMemberDialog.passwordMinPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPw ? t('addHumanMemberDialog.hidePassword') : t('addHumanMemberDialog.showPassword')}
                    title={showPw ? t('addHumanMemberDialog.hidePassword') : t('addHumanMemberDialog.showPassword')}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setForm((s) => ({ ...s, password: generateStrongPassword() }))
                    setShowPw(true)
                    setErrors((x) => ({ ...x, password: undefined }))
                  }}
                >
                  <Wand2 /> {t('addHumanMemberDialog.generatePassword')}
                </Button>
              </div>
              {errors.password ? (
                <p className="text-xs text-destructive">{errors.password}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{t('addHumanMemberDialog.passwordAutoGenHint')}</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? <Loader2 className="animate-spin" /> : <UserPlus />}
                {t('common:actions.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MemberCreatedDialog info={created} onClose={onSummaryClose} />
      {quotaDialog.node}
    </>
  )
}
